import type { ContentEntity, CreatorEntity, FollowingData, MemberData, NonFollowingData, NonMemberData, YouFollowingData } from "shared/schema";
import { type Entity, ValueError, splitEntity, splitOptionalEntity } from "shelving";
import { HELP_ENTITY, NON_FOLLOWING, NON_MEMBER, TAG_ENTITY, YOU_FOLLOWING, getAttemptID, getEarnedID, getMemberID, getOptionalFollowingID, getReactorID, getShortcutID, getUsernameID, isContentType, isCreatorType } from "shared/schema";
import { useItem } from "./db.js";
import { USER } from "./user.js";

// Get specific items with the correct ID format.
export function useUserItem(uid = USER.data.uid) {
	return useItem("user", uid);
}
export function useAttemptItem(cid: string, uid = USER.data.uid) {
	return useItem("attempt", getAttemptID(cid, uid));
}
export function useProfileItem(uid = USER.data.uid) {
	return useItem("profile", uid);
}
export function useEarnedItem(rid: string, uid = USER.data.uid) {
	return useItem("earned", getEarnedID(rid, uid));
}
export function useReactorItem(target: ContentEntity, uid = USER.data.uid) {
	return useItem("reactor", getReactorID(target, uid));
}
export function useMemberItem(gid: string, uid = USER.data.uid) {
	return useItem("member", getMemberID(gid, uid));
}
export function useMemberData(gid: string, uid?: string): MemberData | NonMemberData {
	return useMemberItem(gid, uid).value || NON_MEMBER;
}
export function useUsernameItem(username: string) {
	return useItem("username", getUsernameID(username));
}
export function useShortcutItem(creator: CreatorEntity, slug: string) {
	return useItem("shortcut", getShortcutID(creator, slug));
}
export function useTagNoteItem(slug: string) {
	const shortcut = useItem("shortcut", getShortcutID(TAG_ENTITY, slug));
	const [type, id] = splitOptionalEntity(shortcut.value?.target);
	return useItem("note", type === "note" ? id : undefined);
}
export function useHelpNoteItem(slug: string) {
	const shortcut = useItem("shortcut", getShortcutID(HELP_ENTITY, slug));
	const [type, id] = splitOptionalEntity(shortcut.value?.target);
	return useItem("note", type === "note" ? id : undefined);
}
export function useFollowingData(followed = USER.data.uid, follower = USER.data.uid): FollowingData | NonFollowingData | YouFollowingData {
	const following = useItem("following", getOptionalFollowingID(followed, follower));
	return !following ? YOU_FOLLOWING : following.value || NON_FOLLOWING;
}
export function useFollowerData(follower: string): FollowingData | NonFollowingData | YouFollowingData {
	return useFollowingData(undefined, follower);
}
export function useFollowedData(followed: string): FollowingData | NonFollowingData | YouFollowingData {
	return useFollowingData(followed, undefined);
}
export function useCreatorItem(entity: CreatorEntity) {
	const [type, id] = splitEntity(entity);
	if (!isCreatorType(type)) throw new ValueError("Entity is a not a creator entity", entity);
	return useItem(type, id);
}
export function useCreatedItem(entity: Entity) {
	const [type, id] = splitEntity(entity);
	if (!isContentType(type)) throw new ValueError("Entity is a not a created entity", entity);
	return useItem(type, id);
}
